import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useSession } from '@supabase/auth-helpers-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import EmployerPageShell from '../components/employer/EmployerPageShell';
import Skeleton from '../components/sim/Skeleton';
import SimButton from '../components/ui/SimButton';
import api from '../services/api';
import SimCard from '../sim/ui/SimCard';
import SimShell from '../sim/ui/SimShell';
import { SimBody, SimLabel, SimTitle } from '../sim/ui/SimText';

type WalletResponse = {
  credits_available: number;
  balance?: number;
  reserved_balance?: number;
};

type CreateLinkResponse = {
  link_token: string;
  public_url: string;
};

type SimulationOption = {
  simulationCode: string;
  title: string;
  role: string;
  label: string;
  defaultVersionId: string | null;
  defaultVersionLabel: string | null;
  disabledReason: string | null;
};

type AvailableSimulationsResponse = {
  simulations: Array<{
    simulationCode: string;
    title: string;
    role: string;
    visibility: 'PUBLIC' | 'PRIVATE';
    isGloballyEnabled: boolean;
    hasAccess: boolean;
    defaultVersionId: string | null;
    defaultVersionLabel: string | null;
    disabledReason: string | null;
  }>;
};

const SimCreateLinkPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedSimulation = searchParams.get('simulation');
  const session = useSession();
  const [wallet, setWallet] = useState<WalletResponse | null>(null);
  const [walletLoading, setWalletLoading] = useState(true);
  const [walletError, setWalletError] = useState<string | null>(null);

  const [simulationOptions, setSimulationOptions] = useState<SimulationOption[]>([]);
  const [simulationLoading, setSimulationLoading] = useState(true);
  const [simulationError, setSimulationError] = useState<string | null>(null);
  const [selectedSimulationCode, setSelectedSimulationCode] = useState<string>('');
  const [jobTitle, setJobTitle] = useState('');
  const [expiresAtDate, setExpiresAtDate] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [createdLink, setCreatedLink] = useState<CreateLinkResponse | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [showLinkToken, setShowLinkToken] = useState(false);

  useEffect(() => {
    let mounted = true;
    const currentUserId = session?.user?.id || '';

    const fetchWallet = async () => {
      if (import.meta.env.DEV) {
        console.info('[SIM_CREATE_LINK_WALLET_FETCH_START]', {
          userId: currentUserId || null,
          companyId: session?.user?.app_metadata?.companyId || null,
        });
      }
      try {
        const response = await api.get<WalletResponse>('/sim/wallet');
        if (!mounted) return;
        setWallet(response.data);
        setWalletError(null);
        if (import.meta.env.DEV) {
          console.info('[SIM_CREATE_LINK_WALLET_FETCH_SUCCESS]', {
            userId: currentUserId || null,
            response: response.data,
          });
        }
      } catch (error: any) {
        if (!mounted) return;
        const message =
          error?.response?.data?.error?.message || 'Unable to load wallet balance. Please refresh and try again.';
        setWalletError(message);
        if (import.meta.env.DEV) {
          console.info('[SIM_CREATE_LINK_WALLET_FETCH_ERROR]', {
            userId: currentUserId || null,
            message,
          });
        }
      } finally {
        if (mounted) setWalletLoading(false);
      }
    };

    const fetchAvailableSimulations = async () => {
      try {
        const response = await api.get<AvailableSimulationsResponse>('/sim/available');
        if (!mounted) return;
        const options: SimulationOption[] = (response.data?.simulations || []).map((simulation) => ({
          simulationCode: simulation.simulationCode,
          title: simulation.title,
          role: simulation.role,
          label: `${simulation.title} — ${simulation.role}`,
          defaultVersionId: simulation.defaultVersionId,
          defaultVersionLabel: simulation.defaultVersionLabel,
          disabledReason: simulation.disabledReason,
        }));
        setSimulationOptions(options);
        setSelectedSimulationCode(
          requestedSimulation && options.some((option) => option.simulationCode === requestedSimulation)
            ? requestedSimulation
            : ''
        );
        setSimulationError(null);
      } catch (error: any) {
        if (!mounted) return;
        setSimulationOptions([]);
        const message =
          error?.response?.data?.error?.message || 'Unable to load simulations available to your company.';
        setSimulationError(message);
      } finally {
        if (mounted) {
          setSimulationLoading(false);
        }
      }
    };

    setWallet(null);
    setWalletError(null);
    setWalletLoading(true);
    setSimulationOptions([]);
    setSimulationError(null);
    setSimulationLoading(true);
    fetchWallet();
    fetchAvailableSimulations();

    return () => {
      mounted = false;
    };
  }, [requestedSimulation, session?.user?.id, session?.user?.app_metadata?.companyId]);

  const selectedSimulation = useMemo(
    () =>
      simulationOptions.find(
        (option) =>
          option.simulationCode === selectedSimulationCode && option.simulationCode.trim().length > 0
      ) ?? null,
    [selectedSimulationCode, simulationOptions]
  );

  const canSubmit =
    Boolean(
      selectedSimulationCode &&
        selectedSimulationCode.trim().length > 0 &&
        selectedSimulation?.defaultVersionId
    ) && !submitting;

  const formatExpiresAt = (dateValue: string) => {
    if (!dateValue) return undefined;
    return new Date(`${dateValue}T23:59:59.000Z`).toISOString();
  };

  const handleCreateLink = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(null);
    setCreatedLink(null);

    if (!selectedSimulationCode) {
      setFormError('Select a simulation before creating a link.');
      return;
    }
    if (!selectedSimulation?.defaultVersionId) {
      setFormError('Selected simulation has no active default version for new links.');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        simulation_code: selectedSimulationCode,
        variant_policy: 'ROTATE',
        job_title: jobTitle.trim() || undefined,
        expires_at: formatExpiresAt(expiresAtDate),
      };

      const response = await api.post<CreateLinkResponse>('/sim/links/create', payload);
      setCreatedLink(response.data);
      setShowLinkToken(false);
      toast.success('Simulation link created.');
    } catch (error: any) {
      const message =
        error?.response?.data?.error?.message || 'Unable to create link. Please verify details and try again.';
      setFormError(message);
      toast.error('Failed to create simulation link.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async () => {
    if (!createdLink?.public_url) return;
    try {
      await navigator.clipboard.writeText(createdLink.public_url);
      toast.success('Link copied to clipboard.');
    } catch (_error) {
      toast.error('Could not copy link. Please copy it manually.');
    }
  };

  return (
    <EmployerPageShell activeSection="candidate-links">
      <SimShell
        className="!min-h-0 !bg-transparent !px-0 !py-0"
        maxWidthClassName="max-w-[1180px]"
        fixedAction={
          <SimButton onClick={() => navigate('/employer/dashboard')}>
            Back
          </SimButton>
        }
      >
        <section className="space-y-2">
          <SimLabel className="!text-[15px] !font-semibold !tracking-[0.18em] !text-[#0d9488]">Employer</SimLabel>
          <SimTitle>Create Simulation Link</SimTitle>
          <SimBody>Generate a secure candidate URL for this simulation run.</SimBody>
        </section>
        <div className="grid gap-6 lg:grid-cols-[1.5fr_1fr]">
          <SimCard>
            <form onSubmit={handleCreateLink} className="space-y-5">
              <div className="space-y-2">
                <label className="text-sm font-medium text-txt-secondary">Simulation</label>
                <select
                  value={selectedSimulationCode}
                  onChange={(event) => setSelectedSimulationCode(event.target.value)}
                  className="w-full rounded-input border border-line bg-surface-page/60 px-4 py-3 text-txt-primary focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400"
                >
                  <option value="" disabled>
                    {simulationLoading ? 'Loading simulations...' : 'Select a simulation...'}
                  </option>
                  {simulationOptions.map((option) => (
                    <option
                      key={option.simulationCode}
                      value={option.simulationCode}
                      disabled={!option.defaultVersionId}
                    >
                      {option.label}
                      {!option.defaultVersionId ? ' — Unavailable (no default version)' : ''}
                    </option>
                  ))}
                </select>
                {simulationError ? <p className="text-xs text-rose-200">{simulationError}</p> : null}
                {!simulationLoading && simulationOptions.length === 0 && !simulationError ? (
                  <p className="text-xs text-txt-muted">No simulations are currently available to your company.</p>
                ) : null}
                {selectedSimulation && selectedSimulation.disabledReason ? (
                  <p className="text-xs text-amber-200">{selectedSimulation.disabledReason}</p>
                ) : null}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-txt-secondary">Job reference/title (optional)</label>
                <input
                  type="text"
                  value={jobTitle}
                  onChange={(event) => setJobTitle(event.target.value)}
                  placeholder="e.g., Investment Banker — Q2 Intake"
                  className="w-full rounded-input border border-line bg-surface-page/60 px-4 py-3 text-txt-primary placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-txt-secondary">Expiry date (optional)</label>
                <input
                  type="date"
                  value={expiresAtDate}
                  onChange={(event) => setExpiresAtDate(event.target.value)}
                  className="w-full rounded-input border border-line bg-surface-page/60 px-4 py-3 text-txt-primary focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400"
                />
              </div>

              {formError && (
                <div className="rounded-input border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
                  {formError}
                </div>
              )}

              <SimButton type="submit" disabled={!canSubmit}>
                {submitting ? 'Creating link...' : 'Create Link'}
              </SimButton>
            </form>
          </SimCard>

          <div className="space-y-6">
            <SimCard className="space-y-4">
              <SimLabel>Wallet</SimLabel>
              {walletLoading ? (
                <div className="space-y-3">
                  <Skeleton className="h-8 w-28 bg-slate-700/50" />
                  <Skeleton className="h-4 w-40 bg-slate-700/50" />
                  <Skeleton className="h-4 w-36 bg-slate-700/50" />
                </div>
              ) : walletError ? (
                <p className="text-sm text-rose-200">{walletError}</p>
              ) : (
                <div className="space-y-2">
                  <p className="text-3xl font-semibold text-[#0d9488]">{wallet?.credits_available ?? 0}</p>
                  <p className="text-xs uppercase tracking-[0.2em] text-txt-muted">Credits available</p>
                  <p className="text-sm text-txt-muted">Balance: {wallet?.balance ?? 0}</p>
                  <p className="text-sm text-txt-muted">Reserved: {wallet?.reserved_balance ?? 0}</p>
                </div>
              )}
            </SimCard>

            {createdLink && (
              <SimCard className="space-y-4 border-emerald-500/40 bg-emerald-500/10">
                <SimLabel className="text-emerald-200/90">Link Created</SimLabel>
                <div className="space-y-1">
                  <p className="text-xs uppercase tracking-[0.15em] text-emerald-200/90">Public URL</p>
                  <p className="break-all text-sm font-semibold text-txt-primary">{createdLink.public_url}</p>
                </div>
                <SimButton onClick={handleCopy}>Copy URL</SimButton>
                <div className="pt-1">
                  <button
                    type="button"
                    onClick={() => setShowLinkToken((prev) => !prev)}
                    className="text-xs font-medium text-emerald-200 underline underline-offset-2 hover:text-emerald-100"
                  >
                    {showLinkToken ? 'Hide token' : 'Reveal token'}
                  </button>
                  {showLinkToken && (
                    <div className="mt-2 space-y-1">
                      <p className="text-xs uppercase tracking-[0.15em] text-emerald-200/90">Link Token</p>
                      <p className="break-all font-mono text-xs text-emerald-100">{createdLink.link_token}</p>
                    </div>
                  )}
                </div>
              </SimCard>
            )}

            {selectedSimulation && (
              <SimCard className="space-y-2">
                <SimLabel>Selected Simulation</SimLabel>
                <p className="text-sm text-txt-secondary">{selectedSimulation.label}</p>
                <p className="text-xs text-txt-muted">Code: {selectedSimulation.simulationCode}</p>
                <p className="text-xs text-txt-muted">
                  Default version: {selectedSimulation.defaultVersionLabel || 'Not set'}
                </p>
              </SimCard>
            )}
          </div>
        </div>
      </SimShell>
    </EmployerPageShell>
  );
};

export default SimCreateLinkPage;
